import { RetryOptions } from '@reduxjs/toolkit/dist/query/retry'
import {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  retry,
} from '@reduxjs/toolkit/query'
import { Mutex } from 'async-mutex'
import { AuthToken } from '@typesApp/Customer'
import { config } from '@config/config'
import { authTokenError } from '@redux/slices/connectorSlice'
import { AppState } from '@redux/store'
import {
  createGenericFetchError,
  createInvalidCustomerFetchError,
  createCustomerChangedFetchError,
  CustomerChangedError,
  InvalidCustomerError,
  getErrorMessage,
  isFetchBaseError,
} from '@utils/errorUtil'
import { ApiCache } from './apiCache'
import { authApi } from './auth'
import { dbPrepareHeaders } from './prepareHeaders'
import {
  isRequireReauth,
  ReauthController,
  reauthController,
  ReauthControllerParams,
  ReauthService,
} from './reauth/reauthController'
import { unwrapMutationActionResult } from './unwrap'

const mutex = new Mutex()

const MAX_RETRY_COUNT = 5

const retryOptions: RetryOptions = {
  retryCondition: (error, args, { attempt, extraOptions }): boolean => {
    // retry explicitly disabled by endpoint
    if (extraOptions?.maxRetries === 0) return false

    // 401 handled with reauth
    if (error.status === 401) return false

    // use endpoint specific max retries or default
    const maxRetries =
      typeof extraOptions?.maxRetries === 'number'
        ? Math.max(extraOptions.maxRetries, MAX_RETRY_COUNT)
        : MAX_RETRY_COUNT

    return (
      // NOTE: status will be FETCH_ERROR if some local network conditions (like VPN) changes
      // and we do want to retry again in this case. No harm since we have a max retry count.
      (error.status === 409 || error.status === 504 || error.status === 'FETCH_ERROR') && attempt <= maxRetries
    )
  },
}

export const dbBaseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const baseUrl = config.getApiHost()
  const dbBaseQuery = retry(
    fetchBaseQuery({
      baseUrl,
      prepareHeaders: dbPrepareHeaders,
    }),
    retryOptions
  )

  const relativeUrl = typeof args === 'string' ? args : args.url
  const url = `${baseUrl}${relativeUrl}`
  const apiCache = new ApiCache(api.endpoint)

  // fetch from cache if available
  if (apiCache.isCacheable) {
    const cachedResult = await apiCache.fetchFromCache(url)
    if (cachedResult) {
      return cachedResult
    }
  }

  // wait until the mutex is available without locking it
  await mutex.waitForUnlock()

  let result = await dbBaseQuery(args, api, extraOptions)

  const { isSessionExpired, isLoggingOut } = getSessionState(api)
  if (isRequireReauth(result?.error?.status, isSessionExpired, isLoggingOut)) {
    // checking whether the mutex is locked
    if (!mutex.isLocked()) {
      const releaseMutex = await mutex.acquire()

      try {
        // reauthenticate and retry original request with new token
        const controller = getController(api)
        await controller.reauthOrThrow()
        result = await dbBaseQuery(args, api, extraOptions)
      } catch (error) {
        return handleReauthError(api, error)
      } finally {
        releaseMutex()
      }
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock()
      result = await dbBaseQuery(args, api, extraOptions)
    }
  }

  if (apiCache.isCacheable) {
    apiCache.putInCache(result)
  }

  return result
}

const getSessionState = (api: BaseQueryApi): { isSessionExpired?: boolean; isLoggingOut?: boolean } => {
  const state = api.getState() as AppState
  const isSessionExpired = state.connector?.sessionExpired
  const isLoggingOut = state.connector?.isLoggingOut
  return { isSessionExpired, isLoggingOut }
}

const getController = (api: BaseQueryApi): ReauthController => {
  const state = api.getState() as AppState
  const currentCustomer = state.connector.customer
  const anonymousUserId = state.connector.environment?.anonymousUserId
  const actionModule = state.connector.actionModule
  const { ecommerceCode, ecommerceTransactionHost } = state.connector?.environment ?? {}
  const currentCustomerContext = state.connector?.customerContext

  const params: ReauthControllerParams = {
    ecommerceCode,
    ecommerceTransactionHost,
    currentCustomerContext,
    currentCustomer,
    anonymousUserId,
  }

  if (typeof actionModule?.getCustomer !== 'function') {
    throw new Error('Bad configuration - getCustomer hook missing')
  }

  const service: ReauthService = {
    fetchCustomer: async () => actionModule.getCustomer(),
    fetchAuthToken: async args =>
      unwrapMutationActionResult<AuthToken>(api.dispatch(authApi.endpoints.getAuthToken.initiate(args))),
  }

  return reauthController(service, params)
}

type ErrorReturn = {
  error: FetchBaseQueryError
}

const handleReauthError = (api: BaseQueryApi, error: unknown): ErrorReturn => {
  if (error instanceof InvalidCustomerError) {
    return { error: createInvalidCustomerFetchError('Reauth error - invalid customer', error) }
  }

  if (error instanceof CustomerChangedError) {
    return { error: createCustomerChangedFetchError('Reauth error - customer changed', error) }
  }

  api.dispatch(authTokenError())

  if (isFetchBaseError(error)) {
    return { error }
  } else {
    const message = getErrorMessage(error) ?? 'Reauth error'
    return { error: createGenericFetchError(message) }
  }
}
